import { Button, Card, Popconfirm, Space } from "antd"
import { useState } from "react"
import { useDeleteEmailTemplateMutation, useEmailTemplatesQuery, useSaveEmailTemplateMutation } from "./emailTemplateQueries"
import { EmailTemplateType, EmailTemplateTypes } from "../../common/constants"
import { EmailTemplate, emailTemplateTokens } from "./emailTemplateModel"
import { uniqueId } from "../../helpers/uniqueId"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEdit, faTrash } from "@fortawesome/pro-solid-svg-icons"
import RichTextEditor from "../RichTextEditor/RichTextEditor"
import VSpace from "../common/VSpace"
import EmailTemplateForm from "./EmailTemplateForm"

type Props = {
  emailTemplateType: EmailTemplateType
  onChangesPending?: (hasChanges:boolean) => void
}

const EmailTemplateTypeList = ({ emailTemplateType, onChangesPending }: Props) => {
  const emailTemplates = useEmailTemplatesQuery()
  const saveEmailTemplateMutation = useSaveEmailTemplateMutation()
  const deleteEmailTemplateMutation = useDeleteEmailTemplateMutation()
  const [editingGuids, setEditingGuids] = useState<string[]>([])
  const [addedEmailTemplate, setAddedEmailTemplate] = useState<EmailTemplate | undefined>()

  const filteredTemplates = (emailTemplates.data ?? []).filter(x => x.type === emailTemplateType)
  if (addedEmailTemplate != undefined && addedEmailTemplate.type === emailTemplateType) {
    filteredTemplates.unshift(addedEmailTemplate)
  }
  const tokens = emailTemplateTokens(emailTemplateType)

  const startEditing = (guid: string) => {
    setEditingGuids(x => [...x, guid])
    onChangesPending?.(true)
  }

  const saveEmailTemplate = (emailTemplate: EmailTemplate) => {
    saveEmailTemplateMutation.mutate(emailTemplate, {
      onSuccess: () => {
        stopEditingGuid(emailTemplate.guid)
        onChangesPending?.(editingGuids.length >= 1 || addedEmailTemplate !== undefined)

        if (addedEmailTemplate?.guid === emailTemplate.guid) {
          setAddedEmailTemplate(undefined)
        }
      }
    })
  }

  const stopEditingGuid = (guid: string) => {
    setEditingGuids(x => x.filter(y => y !== guid))
    if(guid === addedEmailTemplate?.guid){
      setAddedEmailTemplate(undefined)
    }
  }

  const addEmailTemplate = () => {
    setAddedEmailTemplate({ id: 0, guid: uniqueId(), type: emailTemplateType, name: 'New Email Template' })
    onChangesPending?.(true)
  }

  const handleDelete = (guid: string) => {
    deleteEmailTemplateMutation.mutate(guid)
  }

  return <VSpace size="large">
    <Button type="primary" onClick={addEmailTemplate} >Add New</Button>
    {filteredTemplates.map(item => {
      const isNew = addedEmailTemplate?.guid === item.guid
      return <Card
        key={item.guid}
        title={item.name}
        extra={<Space>
          {!isNew && <Button type="link" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => startEditing(item.guid)} />}
          <Popconfirm title="Are you sure you want to delete this email template?" onConfirm={() => handleDelete(item.guid)}>
            <Button danger type="link" icon={<FontAwesomeIcon icon={faTrash} />} />
          </Popconfirm>
        </Space>}
        style={{ width: '100%' }}
      >
        {!editingGuids.includes(item.guid) && !isNew &&
          <VSpace size="large">
            <VSpace size="small">
              <strong>Subject:</strong>
              <RichTextEditor value={item.emailSubject ?? ''} disabled removeToolbar tokens={tokens} />
            </VSpace>

            <VSpace size="small">
              <strong>Email Body:</strong>
              <RichTextEditor value={item.emailBody ?? ''} disabled removeToolbar tokens={tokens} />
            </VSpace>

            {emailTemplateType === EmailTemplateTypes.Consultation && <VSpace size="small">
              <strong>Event Description:</strong>
              <RichTextEditor value={item.eventDescription ?? ''} disabled removeToolbar />
            </VSpace>}
          </VSpace>
        }

        {editingGuids.includes(item.guid) && !isNew &&
          <EmailTemplateForm emailTemplate={item} onCancel={() => stopEditingGuid(item.guid)} onSubmit={saveEmailTemplate} />
        }

        {isNew &&
          <EmailTemplateForm emailTemplate={item} onCancel={() => stopEditingGuid(item.guid)} onSubmit={saveEmailTemplate} />
        }
      </Card>
    })}
  </VSpace>
}

export default EmailTemplateTypeList
